import { apiClient } from './client';

export type SyncJobKind = 'structure' | 'pages' | 'insights';
export type SyncJobStatus = 'queued' | 'running' | 'ok' | 'partial' | 'failed';

export interface SyncJobRow {
  id: number;
  platform_token_id: number;
  kind: SyncJobKind;
  status: SyncJobStatus;
  ad_account_id: number | null;
  ad_account_name: string | null;
  platform_code: string;
  platform_name: string;
  queued_at: string;
  started_at: string | null;
  finished_at: string | null;
  counts: Record<string, number> | null;
  error_message: string | null;
  start_date: string | null;
  end_date: string | null;
  attempt_count: number;
  created_at: string;
}

export interface SyncJobQuery {
  platform_token_id?: number;
  kind?: SyncJobKind;
  status?: SyncJobStatus;
  page?: number;
  per_page?: number;
}

export interface SyncJobListResponse {
  data: SyncJobRow[];
  total: number;
  page: number;
  per_page: number;
}

export async function listSyncJobs(q: SyncJobQuery = {}): Promise<SyncJobListResponse> {
  const { data } = await apiClient.get<SyncJobListResponse>('/sync-jobs', { params: q });
  return data;
}

export interface SyncStatus {
  pending: number;
  recent_failed: number;
}

export async function getSyncStatus(): Promise<SyncStatus> {
  const { data } = await apiClient.get<SyncStatus>('/sync-jobs/status');
  return data;
}

export async function retrySyncJob(id: number): Promise<{ id: number }> {
  const { data } = await apiClient.post<{ id: number }>(`/sync-jobs/${id}/retry`);
  return data;
}

export interface SyncCoverageInsight {
  min_date: string | null;
  max_date: string | null;
  rows: number;
  spend: number;
  last_updated_at: string | null;
}

export interface SyncCoverageJobState {
  queued: number;
  running: number;
  ok: number;
  partial: number;
  failed: number;
  last_queued_at: string | null;
  last_finished_at: string | null;
}

export interface SyncCoverageRow {
  id: number;
  name: string;
  platform_account_id: string;
  platform_code: string;
  platform_name: string;
  status: string;
  currency: string;
  timezone: string | null;
  synced_at: string | null;
  structure_counts: {
    campaigns: number;
    ad_sets: number;
    ads: number;
    creatives: number;
  };
  insights: Partial<Record<'account' | 'campaign' | 'adset' | 'ad', SyncCoverageInsight>>;
  jobs_7d: Partial<Record<SyncJobKind, SyncCoverageJobState>>;
  integrity?: {
    status: 'healthy' | 'repairing' | 'blocked' | 'skipped';
    issues: Array<{
      code: string;
      level?: 'account' | 'campaign' | 'adset' | 'ad';
      message: string;
    }>;
    pending_repairs: number;
  };
}

export async function getSyncCoverage(): Promise<{ data: SyncCoverageRow[] }> {
  const { data } = await apiClient.get<{ data: SyncCoverageRow[] }>('/sync-jobs/coverage');
  return data;
}

export interface ActivityFeed {
  sync_jobs: Array<{
    id: number;
    kind: string;
    status: string;
    ad_account_id: number | null;
    ad_account_name: string | null;
    platform_code: string;
    queued_at: string;
    finished_at: string | null;
    error_message: string | null;
  }>;
  automation_executions: Array<{
    id: number;
    rule_id: number;
    rule_name: string;
    action_type: string;
    result: string;
    error_message: string | null;
    evaluated_at: string;
  }>;
  commerce_syncs: Array<{
    id: number;
    shop_name: string;
    provider: string;
    last_synced_at: string;
  }>;
  test_plans: Array<{
    id: number;
    title: string;
    created_at: string;
    duration_ms: number;
    provider: string;
    model: string;
  }>;
}

export async function getActivityFeed(): Promise<ActivityFeed> {
  const { data } = await apiClient.get<ActivityFeed>('/sync-jobs/activity');
  return data;
}
